/**
 * A minimal hyperscript helper. `el("div", { class: "card" }, "hello")` builds
 * DOM directly — no virtual DOM, no framework. Enough to compose the lab.
 */

type Child = Node | string | number | null | undefined | false | Child[];

export interface Props {
  class?: string;
  id?: string;
  style?: Partial<CSSStyleDeclaration> | string;
  html?: string;
  dataset?: Record<string, string>;
  attrs?: Record<string, string | number | boolean>;
  [key: string]: unknown; // on* handlers and plain attributes
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Props | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (value == null) continue;
      if (key === "class") node.className = String(value);
      else if (key === "id") node.id = String(value);
      else if (key === "html") node.innerHTML = String(value);
      else if (key === "style") {
        if (typeof value === "string") node.setAttribute("style", value);
        else Object.assign(node.style, value);
      } else if (key === "dataset") {
        Object.assign(node.dataset, value as Record<string, string>);
      } else if (key === "attrs") {
        for (const [a, v] of Object.entries(value as Record<string, unknown>)) {
          node.setAttribute(a, String(v));
        }
      } else if (key.startsWith("on") && typeof value === "function") {
        node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
      } else if (typeof value === "boolean") {
        if (value) node.setAttribute(key, "");
      } else {
        node.setAttribute(key, String(value));
      }
    }
  }

  for (const child of children) appendOne(node, child);
  return node;
}

/** Append one or more children (arrays are flattened; null/false are skipped). */
export function append(parent: Node, ...children: Child[]): void {
  for (const child of children) appendOne(parent, child);
}

function appendOne(parent: Node, child: Child): void {
  if (child == null || child === false) return;
  if (Array.isArray(child)) {
    for (const c of child) appendOne(parent, c);
  } else if (child instanceof Node) {
    parent.appendChild(child);
  } else {
    parent.appendChild(document.createTextNode(String(child)));
  }
}

/** Remove all children of a node. */
export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Namespaced element creation for SVG. */
export function svgEl(tag: string, attrs?: Record<string, string | number>): SVGElement {
  const node = document.createElementNS("http://www.w3.org/2000/svg", tag);
  if (attrs) for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}
